import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api } from '../api/api';
import Input from './Input';
import Button from './Button';
import CustomDropdown from './CustomDropdown';
import { useApiCall } from '../hooks/useApiCall';
import { useClothesStore } from '../store/clothesStore';

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const ModalContent = styled.div`
  background-color: #0E1F1F;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  border: 2px solid #284640;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: #FFFFFF;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #838D90;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #FFFFFF;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FormField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #FFFFFF;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #24BA7D;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: flex-end;
`;

const SearchSection = styled.div`
  margin-bottom: 20px;
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ErrorMessage = styled.div`
  color: #FF6B6B;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ProductPreview = styled.div`
  background-color: #172A2A;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const ProductImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 8px;
  object-fit: contain;
  background-color: #284640;
  align-self: center;
`;

const ProductName = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #FFFFFF;
  text-align: center;
`;

const ProductColor = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #609F94;
  text-align: center;
`;

interface ProductInfo {
  model: string;
  color: string;
  photo_url: string;
  brand?: string;
}

import type { IMetroStation } from "../api/api";

interface AddClothesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddClothesModal: React.FC<AddClothesModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    brand: '',
    size: '',
    price: '',
    article: '',
    condition: '',
    city: '',
    place: '',
    fitting: false
  });

  const [brands, setBrands] = useState<Array<{value: string, label: string}>>([]);
  const [conditions, setConditions] = useState<Array<{value: string, label: string}>>([]);
  const [sizes, setSizes] = useState<Array<{value: string, label: string}>>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [metroStations, setMetroStations] = useState<string[]>([]);
  
  // Состояния для поиска продукта
  const [article, setArticle] = useState('');
  const [productInfo, setProductInfo] = useState<ProductInfo | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [lastSearchedArticle, setLastSearchedArticle] = useState('');

  const { executeApiCall } = useApiCall();
  const { createClothes } = useClothesStore();

  // Загружаем бренды, состояния и размеры при открытии модалки
  useEffect(() => {
    if (isOpen) {
      loadBrands();
      loadConditions();
      loadSizes();
      loadCities();
      loadMetroStations();
    }
  }, [isOpen]);

  // Поиск продукта по артикулу с debounce
  useEffect(() => {
    const searchProduct = async () => {
      if (!article.trim()) {
        setProductInfo(null);
        setValidationError('');
        setLastSearchedArticle('');
        return;
      }

      // Не делаем запрос если уже искали этот артикул
      if (lastSearchedArticle === article.trim()) {
        return;
      }

      setIsSearching(true);
      setValidationError('');
      setLastSearchedArticle(article.trim());

      const result = await executeApiCall(
        () => api.loadProductInfo(article.trim()),
        {
          showSuccessToast: false,
          showErrorToast: false
        }
      );

      if (result && result.model?.trim()) {
        const color = result.color?.trim() ? result.color : 'NA';
        setProductInfo({ ...result, color });
        // Автозаполнение формы
        if (result.brand?.trim()) {
          const apiBrand = result.brand.trim();
          
          // Ищем точное совпадение
          let matchedBrand = brands.find(b => b.value === apiBrand);
          
          // Если точного совпадения нет, ищем по регистронезависимому сравнению
          if (!matchedBrand) {
            matchedBrand = brands.find(b => 
              b.value.toLowerCase() === apiBrand.toLowerCase()
            );
          }
          
          // Если не нашли совпадение, добавляем API бренд в список
          if (!matchedBrand && apiBrand) {
            const newBrandOption = { value: apiBrand, label: apiBrand };
            setBrands(prev => [...prev, newBrandOption]);
          }
          
          const finalBrand = matchedBrand ? matchedBrand.value : apiBrand;
          
          setFormData(prev => ({
            ...prev,
            article: article.trim(),
            brand: finalBrand
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            article: article.trim()
          }));
        }
      } else {
        setValidationError('Не удалось найти товар по артикулу');
        setProductInfo(null);
      }

      setIsSearching(false);
    };

    const timeoutId = setTimeout(searchProduct, 800);
    return () => clearTimeout(timeoutId);
  }, [article]); // Убрали executeApiCall из зависимостей

  const loadBrands = async () => {
    try {
      const response = await api.getBrands();
      if (response && response.brands) {
        setBrands(response.brands.map((brand: any) => ({ 
          value: brand.name, 
          label: brand.name 
        })));
      }
    } catch (error) {
      console.error('Ошибка загрузки брендов:', error);
    }
  };

  const loadConditions = async () => {
    try {
      const response = await api.getConditions();
      if (response && response.conditions) {
        setConditions(response.conditions.map((condition: any) => ({ 
          value: condition.name, 
          label: condition.name 
        })));
      }
    } catch (error) {
      console.error('Ошибка загрузки состояний:', error);
    }
  };

  const loadSizes = async () => {
    try {
      const response = await api.getSizes();
      if (response && response.sizes) {
        setSizes(response.sizes.map((size: any) => ({ 
          value: size.name, 
          label: size.name 
        })));
      }
    } catch (error) {
      console.error('Ошибка загрузки размеров:', error);
    }
  };

  const loadCities = async () => {
    try {
      const response = await api.getAvailableCities();
      if (response && response.cities) {
        setCities(response.cities);
      }
    } catch (error) {
      console.error('Ошибка загрузки городов:', error);
    }
  };

  const loadMetroStations = async () => {
    try {
      const response = await api.getMetroStations();
      if (response && response.stations) {
        setMetroStations(response.stations);
      }
    } catch (error) {
      console.error('Ошибка загрузки станций метро:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.brand || !formData.size || !formData.price || !formData.article) {
      return;
    }

    const clothesData = {
      brand: formData.brand,
      size: formData.size,
      price: parseFloat(formData.price),
      article: formData.article,
      condition: formData.condition || 'Новое',
      city: formData.city || '',
      place: formData.place || '',
      fitting: formData.fitting
    };

    await executeApiCall(
      () => createClothes(clothesData),
      {
        successMessage: 'Одежда успешно добавлена в инвентарь',
        errorMessage: 'Ошибка при добавлении одежды'
      }
    );

    onClose();
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      brand: '',
      size: '',
      price: '',
      article: '',
      condition: '',
      city: '',
      place: '',
      fitting: false
    });
    setArticle('');
    setProductInfo(null);
    setValidationError('');
    setLastSearchedArticle('');
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Добавить одежду</ModalTitle>
          <CloseButton onClick={handleClose}>
            ×
          </CloseButton>
        </ModalHeader>

        <SearchSection>
          <SearchContainer>
            <Input
              label="Артикул"
              value={article}
              onChange={(e) => setArticle(e.target.value)}
              placeholder="Введите артикул для поиска товара"
            />
            {isSearching && (
              <div style={{ color: '#609F94', fontSize: '14px', marginTop: '8px' }}>
                Поиск товара...
              </div>
            )}
          </SearchContainer>
        </SearchSection>

        {validationError && <ErrorMessage>{validationError}</ErrorMessage>}

        {productInfo && (
          <ProductPreview>
            <ProductImage src={productInfo.photo_url} alt={productInfo.model} />
            <ProductName>{productInfo.model}</ProductName>
            <ProductColor>{productInfo.color}</ProductColor>
            <Button 
              variant="secondary" 
              onClick={() => {
                setArticle('');
                setProductInfo(null);
                setFormData(prev => ({ ...prev, article: '' }));
                setLastSearchedArticle('');
              }}
              style={{ marginTop: '8px' }}
            >
              Очистить поиск
            </Button>
          </ProductPreview>
        )}

        <FormContainer>
          <FormRow>
            <FormField>
              <CustomDropdown
                label="Бренд"
                options={brands}
                value={formData.brand}
                onChange={(value) => handleInputChange('brand', value)}
                placeholder="Выберите бренд"
                required
              />
            </FormField>
            <FormField>
              <CustomDropdown
                label="Размер"
                options={sizes}
                value={formData.size}
                onChange={(value) => handleInputChange('size', value)}
                placeholder="Выберите размер"
                required
              />
            </FormField>
          </FormRow>

          <FormRow>
            <FormField>
              <FieldLabel>Цена *</FieldLabel>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                placeholder="Введите цену"
              />
            </FormField>
            <FormField>
              <Input
                label="Артикул"
                value={formData.article}
                onChange={(e) => handleInputChange('article', e.target.value)}
                placeholder="Введите артикул"
                disabled={!!productInfo}
              />
            </FormField>
          </FormRow>

          <FormRow>
            <FormField>
              <CustomDropdown
                label="Состояние"
                options={conditions}
                value={formData.condition}
                onChange={(value) => handleInputChange('condition', value)}
                placeholder="Выберите состояние"
              />
            </FormField>
          </FormRow>

          <FormRow>
            <FormField>
              <CustomDropdown
                label="Город"
                options={cities.map((city) => ({
                  value: city,
                  label: city,
                }))}
                value={formData.city}
                onChange={(value) => handleInputChange('city', value)}
                placeholder="Выберите город"
              />
            </FormField>
            <FormField>
              <CustomDropdown
                label="Место встречи (станция метро)"
              options={metroStations.map((station) => ({
                value: station,
                label: station,
              }))}
                value={formData.place}
                onChange={(value) => handleInputChange('place', value)}
                placeholder="Выберите станцию метро"
              />
            </FormField>
          </FormRow>

          <FormField>
            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                checked={formData.fitting}
                onChange={(e) => handleInputChange('fitting', e.target.checked)}
              />
              <FieldLabel>Возможна примерка</FieldLabel>
            </CheckboxContainer>
          </FormField>

          <ButtonContainer>
            <Button variant="secondary" onClick={handleClose}>
              Отмена
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSubmit}
              disabled={!formData.brand || !formData.size || !formData.price || !formData.article}
            >
              Добавить
            </Button>
          </ButtonContainer>
        </FormContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddClothesModal;
